import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, IconButton, CircularProgress, Pagination, Tooltip, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CommentOutlined from '@mui/icons-material/CommentOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getLeadSchedulesDashboard, getLeadDashboard, getLeadScheduleById, deleteLeadSchedule, setStoreData } from '../../../Redux/actions';
import { updateParams } from '../../../utils';
import { paginationStyle, datePickerSlotProps, buttonStyles } from '../../../theme/formStyles';
import SearchBox from '../../Common/SearchBox';
import LeadScheduleForm from './LeadScheduleForm';

const statusStyle = (status) => status === 'Closed'
  ? { color: '#196a3e', bgcolor: '#f1faf4', border: '1px solid #c2e9cf' }
  : { color: '#8f6d1d', bgcolor: '#fffdee', border: '1px solid #eed699' };

const validateToDate = (fromDate, toDate) =>
  fromDate && toDate && toDate < fromDate
    ? "'To Date' must be equal to or later than 'From Date'"
    : '';

const itemsPerPage = 20;

export default function LeadScheduleTab({ refreshKey }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const existingParams = Object.fromEntries([...queryParams]);

  const [view, setView] = useState('list');
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [totalPages, setTotalPages] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [scheduleFromDate, setScheduleFromDate] = useState(existingParams?.scheduleFromDate || '');
  const [scheduleToDate, setScheduleToDate] = useState(existingParams?.scheduleToDate || '');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (existingParams?.page) {
      return;
    }
    updateQueryParams({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, refreshKey, internalRefreshKey]);

  useEffect(() => {
    setScheduleFromDate(existingParams?.scheduleFromDate || '');
    setScheduleToDate(existingParams?.scheduleToDate || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const fetchAll = () => {
    setLoading(true);
    const page = existingParams?.page || 1;
    const scheduleParams = {
      page,
      search: existingParams?.search || '',
      scheduleFromDate: existingParams?.scheduleFromDate || '',
      scheduleToDate: existingParams?.scheduleToDate || '',
    };
    Promise.all([
      dispatch(getLeadSchedulesDashboard(scheduleParams)),
      dispatch(getLeadDashboard()),
    ]).then(([scheduleRes, leadRes]) => {
      if (scheduleRes?.data?.success) {
        setSchedules(scheduleRes.data.data || []);
        setTotalPages(scheduleRes.data.totalPages);
        setTotalCount(scheduleRes.data.totalItems);
        setCurrentPage(scheduleRes.data.currentPage);
      }
      if (leadRes?.data?.success) setLeads(leadRes.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const updateQueryParams = (params) => {
    const searchParams = updateParams(existingParams, params);
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const handlePageChange = (event, newPage) => {
    updateQueryParams({ page: newPage });
  };

  const refresh = () => setInternalRefreshKey(k => k + 1);

  const availableLeads = leads.filter(lead => !lead.has_schedule);

  const handleSearch = (value) => {
    updateQueryParams({ search: value, page: 1 });
  };

  const handleApplyDateFilter = () => {
    const error = validateToDate(scheduleFromDate, scheduleToDate);
    if (error) {
      setDateError(error);
      return;
    }
    updateQueryParams({ scheduleFromDate, scheduleToDate, page: 1 });
  };

  const handleClearDateFilter = () => {
    setScheduleFromDate('');
    setScheduleToDate('');
    setDateError('');
    updateQueryParams({ scheduleFromDate: '', scheduleToDate: '', page: 1 });
  };

  const handleCreate = () => {
    setEditingSchedule(null);
    setView('form');
  };

  const handleEdit = (schedule) => {
    setLoadingEditId(schedule.id);
    dispatch(getLeadScheduleById(schedule.id)).then((res) => {
      setLoadingEditId(null);
      if (res?.status === 200 && res.data?.success && res.data.data) {
        setEditingSchedule(res.data.data);
        setView('form');
      }
    }).catch(() => setLoadingEditId(null));
  };

  const handleBack = () => {
    setView('list');
    setEditingSchedule(null);
  };

  const handleDeleteClick = (schedule) => {
    setDeletingSchedule(schedule);
  };

  const handleConfirmDelete = () => {
    setDeleteLoading(true);
    dispatch(deleteLeadSchedule(deletingSchedule.id)).then((res) => {
      setDeleteLoading(false);
      if (res?.status === 200 && res.data?.success) {
        setDeletingSchedule(null);
        refresh();
      } else {
        dispatch(setStoreData('alertMessage', {
          msg: res?.data?.message || 'Something went wrong',
          type: 'fail',
          handleClose: () => dispatch(setStoreData('alertMessage', null)),
        }));
      }
    }).catch(() => setDeleteLoading(false));
  };

  const handleSaved = () => {
    setView('list');
    setEditingSchedule(null);
    refresh();
  };

  if (view === 'form') {
    return (
      <LeadScheduleForm
        editingSchedule={editingSchedule}
        leads={editingSchedule ? leads : availableLeads}
        onBack={handleBack}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={availableLeads.length === 0}
          sx={{ backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' }, textTransform: 'none' }}
        >
          + Create schedule
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', pr: 1 }}>Search By:</Typography>
            <SearchBox
              value={existingParams?.search || ''}
              placeholder="Client / Opportunity / Facility"
              search={handleSearch}
            />
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <DatePicker
                label="From Date"
                format="DD/MM/YYYY"
                value={scheduleFromDate ? dayjs(scheduleFromDate) : null}
                onChange={(newVal) => {
                  const formatted = newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '';
                  setScheduleFromDate(formatted);
                  setDateError(validateToDate(formatted, scheduleToDate));
                }}
                slotProps={datePickerSlotProps({ size: 'small' })}
              />
              <DatePicker
                label="To Date"
                format="DD/MM/YYYY"
                value={scheduleToDate ? dayjs(scheduleToDate) : null}
                minDate={scheduleFromDate ? dayjs(scheduleFromDate) : undefined}
                onChange={(newVal) => {
                  const formatted = newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '';
                  setScheduleToDate(formatted);
                  setDateError(validateToDate(scheduleFromDate, formatted));
                }}
                slotProps={datePickerSlotProps({ size: 'small', error: !!dateError, helperText: dateError })}
              />
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyDateFilter}
                disabled={!!dateError || (!scheduleFromDate && !scheduleToDate)}
                sx={{ backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' }, textTransform: 'none' }}
              >
                Apply
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearDateFilter}
                disabled={!scheduleFromDate && !scheduleToDate}
                sx={{ borderColor: '#008080', color: '#008080', textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #eaeaea' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['SI No', 'Client Name', 'Facility', 'Opportunity Name', 'Location', 'Date', 'BD Owner', 'Meeting Type', 'Description', 'Status', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#008080' }} />
                  </TableCell>
                </TableRow>
              ) : schedules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3, color: '#888' }}>
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : schedules.map((schedule, index) => (
                <TableRow
                  key={schedule.id}
                  onMouseEnter={() => setHoveredRowId(schedule.id)}
                  onMouseLeave={() => setHoveredRowId(null)}
                  sx={{ '& td': { borderBottom: '1px solid #f0f0f0', py: 1.2 } }}
                >
                  <TableCell>{(currentPage - 1) * itemsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{schedule.lead?.client_name}</TableCell>
                  <TableCell>{schedule.lead?.facility_name || '-'}</TableCell>
                  <TableCell>{schedule.lead?.opportunity_name}</TableCell>
                  <TableCell>{schedule.location || '-'}</TableCell>
                  <TableCell>{schedule.schedule_date ? new Date(schedule.schedule_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                  <TableCell>{schedule.bd_owner?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip label={schedule.meeting_type} size="small" sx={{ fontWeight: 500, color: '#1a6894', bgcolor: '#f1f8fc', border: '1px solid #b8ddec' }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>
                        {schedule.status === 'Closed'
                          ? (schedule.closing_comment || '-')
                          : (schedule.description || '-')}
                      </span>
                      {schedule.entries?.length > 0 && (
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              {schedule.entries.map((entry, idx) => (
                                <Box key={idx} sx={{ mb: idx < schedule.entries.length - 1 ? 1.5 : 0 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#ffd700', display: 'block', mb: 0.5 }}>
                                    {entry.schedule_date ? new Date(entry.schedule_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'} &middot; {entry.meeting_type}
                                  </Typography>
                                  {entry.description && (
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4, display: 'block' }}>
                                      {entry.description}
                                    </Typography>
                                  )}
                                  {entry.status === 'Closed' && entry.closing_comment && (
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4, fontStyle: 'italic', display: 'block' }}>
                                      Closing comment: {entry.closing_comment}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          }
                          arrow
                          placement="right"
                        >
                          <CommentOutlined sx={{ fontSize: '1rem', color: '#666', cursor: 'pointer', '&:hover': { color: '#008080' } }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={schedule.status} size="small" sx={{ fontWeight: 500, ...statusStyle(schedule.status) }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', opacity: hoveredRowId === schedule.id ? 1 : 0 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(schedule)}
                          disabled={loadingEditId === schedule.id}
                        >
                          {loadingEditId === schedule.id ? (
                            <CircularProgress size={16} sx={{ color: '#008080' }} />
                          ) : (
                            <EditIcon fontSize="small" sx={{ color: 'black' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteClick(schedule)}>
                          <DeleteIcon fontSize="small" sx={{ color: '#c62828' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {schedules.length > 0 && (
        <Grid container item xs={12} style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '24px' }}>
            <span style={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
              {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}-
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
            </span>
          </Grid>
          <Pagination
            page={currentPage}
            count={totalPages}
            variant="outlined"
            shape="rounded"
            sx={paginationStyle}
            onChange={handlePageChange}
          />
        </Grid>
      )}

      {availableLeads.length === 0 && leads.length > 0 && (
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>
          Every lead already has a schedule — edit an existing one above to add a follow-up entry.
        </Typography>
      )}

      <Dialog open={!!deletingSchedule} onClose={() => setDeletingSchedule(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#222' }}>Delete this schedule?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666' }}>
            This will permanently remove the schedule and its full history for{' '}
            <strong>{deletingSchedule?.lead?.client_name}</strong> &middot; {deletingSchedule?.lead?.opportunity_name}.
            This action can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeletingSchedule(null)} variant="outlined" sx={buttonStyles.outlined}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={{ backgroundColor: '#c62828', '&:hover': { backgroundColor: '#a41f1f' } }}
          >
            {deleteLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
